import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/shared/components/ui/dialog'
import { apiClient } from '@/shared/lib/api/api-client'
import { cn } from '@/shared/lib/utils'
import { WizardCountryStep } from './wizard-country-step'
import { WizardConfigureStep } from './wizard-configure-step'
import type { CountryWithInstitutions, WizardHeaderCountry } from '../lib/countries-with-institutions'
import { ConnectivityType, connectivityValueFromLabel, type ConnectivityValue } from '../lib/account-connectivity'
import type { BankAccount } from '../types/bank-account'

type WizardStep = 'country' | 'configure'

interface AddAccountWizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  editingAccount?: BankAccount | null
}

function headerCountryForAccount(editingAccount: BankAccount): WizardHeaderCountry | null {
  const { institutionCountryCode, institutionCountryName } = editingAccount
  if (!institutionCountryCode || !institutionCountryName) return null
  return { CountryCode: institutionCountryCode, CountryName: institutionCountryName }
}

/**
 * Orchestrates the unified add/edit account wizard: country selection, then a
 * connectivity choice that routes to either the Open Banking bank list or the
 * shared account form. Edit mode skips the country grid and never selects a
 * country: its header shows the institution's country as reported by the
 * backend, or nothing at all when the account has none (UC-047).
 */
export function AddAccountWizard({ open, onOpenChange, editingAccount = null }: AddAccountWizardProps) {
  const [step, setStep] = useState<WizardStep>('country')
  const [selectedCountry, setSelectedCountry] = useState<CountryWithInstitutions | null>(null)
  const [connectivity, setConnectivity] = useState<ConnectivityValue>(ConnectivityType.ManualUpload)
  const [search, setSearch] = useState('')
  const [isBusy, setIsBusy] = useState(false)
  const initializedEditAccountId = useRef<number | null>(null)

  const handleBusyChange = useCallback((busy: boolean) => setIsBusy(busy), [])

  const { data: countries = [] } = useQuery<CountryWithInstitutions[]>({
    queryKey: ['countries-with-institutions'],
    queryFn: () => apiClient.get<CountryWithInstitutions[]>('/api/countries/WithInstitutions'),
    staleTime: 10 * 60 * 1000,
    enabled: open,
  })

  useEffect(() => {
    // Edit mode does not depend on the countries query, so the form opens
    // without waiting for that fetch.
    if (!open || !editingAccount) return
    if (initializedEditAccountId.current === editingAccount.id) return
    initializedEditAccountId.current = editingAccount.id
    setConnectivity(connectivityValueFromLabel(editingAccount.connectivity))
    setStep('configure')
  }, [open, editingAccount])

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setStep('country')
      setSelectedCountry(null)
      setConnectivity(ConnectivityType.ManualUpload)
      setSearch('')
      initializedEditAccountId.current = null
    }
    onOpenChange(nextOpen)
  }

  function handleCountrySelect(country: CountryWithInstitutions) {
    setSelectedCountry(country)
    setSearch('')
    setStep('configure')
  }

  function handleBack() {
    setStep('country')
    setSelectedCountry(null)
  }

  function handleDone() {
    handleOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={isBusy ? undefined : handleOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] flex flex-col p-0">
        {/* On the configure step the visible header is the back + flag + country row inside
            WizardConfigureStep, so the dialog title/description are kept for a11y only (sr-only). */}
        <DialogHeader className={cn('px-6 pt-6 pb-0 shrink-0', step === 'configure' && 'sr-only')}>
          <DialogTitle>{step === 'country' ? 'Add Account' : 'Configure account'}</DialogTitle>
          <DialogDescription>Select a country and connection type to add a bank account.</DialogDescription>
        </DialogHeader>

        <div className={cn('flex-1 overflow-y-auto px-6 pb-6 min-h-0', step === 'configure' && 'pt-6')}>
          {step === 'configure' && (selectedCountry || editingAccount) ? (
            <WizardConfigureStep
              country={selectedCountry}
              headerCountry={editingAccount ? headerCountryForAccount(editingAccount) : selectedCountry}
              connectivity={connectivity}
              onConnectivityChange={setConnectivity}
              editingAccount={editingAccount}
              isEditing={editingAccount !== null}
              onBack={handleBack}
              onDone={handleDone}
              onBusyChange={handleBusyChange}
            />
          ) : (
            <WizardCountryStep
              countries={countries}
              search={search}
              onSearchChange={setSearch}
              onSelect={handleCountrySelect}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
